/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Typography,
    Box,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Icon,
    Paper,
    Chip,
} from "@mui/material";
import {
    Image,
    PictureAsPdf,
    PlayCircle,
    YouTube,
    InsertDriveFile,
    Link,
    Replay,
    Visibility,
    Close,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import moment from "moment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { Snackbar, Alert } from "@mui/material";
import { Download, Description } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const BroadcastHistory = ({ onEdit }) => {
    const location = useLocation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedBroadcast, setSelectedBroadcast] = useState(null);
    const [viewPdfUrl, setViewPdfUrl] = useState(null);
    const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
    const [recipientList, setRecipientList] = useState([]);
    const [message, setMessage] = useState({ status: "", title: "", msg: "" });

    // Fetch current user for role check
    const { data: user } = useQuery({
        queryKey: ["auth-me"],
        queryFn: async () => {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/auth/me`,
                {},
                { withCredentials: true }
            );
            return response.data;
        },
        retry: false,
    });

    useEffect(() => {
        if (user?.user_id) fetchHistory();
    }, [user]);

    const fetchHistory = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/email/broadcasts`, {
                params: {
                    user_id: user?.user_id,
                },
                withCredentials: true,
            });
            if (response.data && response.data.data) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch broadcast history", error);
        } finally {
            setLoading(false);
        }
    };

    // Handle auto-preview from notification
    useEffect(() => {
        if (location.state?.fromNotification && location.state?.broadcastId && data.length > 0) {
            const broadcast = data.find((item) => String(item.id) === String(location.state.broadcastId));
            if (broadcast) {
                setSelectedBroadcast(broadcast);
                setPreviewOpen(true);
            }
        }
    }, [data, location.state]);

    const handlePreview = (url) => {
        if (!url) return;
        const cleanUrl = url.split('?')[0].toLowerCase();
        const isWord = cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx');

        if (isWord) {
            const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
            setViewPdfUrl(officeUrl);
        } else {
            setViewPdfUrl(url);
        }
    };

    const handleDownload = async (url, title) => {
        if (!url) return;
        try {
            const cleanUrl = url.split('?')[0].toLowerCase();
            const originalExt = cleanUrl.split('.').pop();
            const response = await fetch(url);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `${title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD')}.${originalExt}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setMessage({ status: "success", title: "Success", msg: "File downloaded successfully!" });
        } catch (error) {
            console.error("Download failed", error);
            setMessage({ status: "error", title: "Error", msg: "Download failed. Please try again." });
        }
    };

    const DocumentAction = ({ url, title, isPdf, isWord, isImage }) => {
        if (!url) return <MDTypography variant="caption" color="text">-</MDTypography>;
        return (
            <MDBox display="flex" gap={1} alignItems="center">
                {isPdf ? <PictureAsPdf sx={{ fontSize: '1rem', color: '#f44336' }} /> :
                    isWord ? <Description sx={{ fontSize: '1rem', color: '#1976d2' }} /> :
                        isImage ? <Image sx={{ fontSize: '1rem', color: '#4caf50' }} /> : null}
                <Tooltip title="View">
                    <IconButton
                        size="small"
                        color="info"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePreview(url);
                        }}
                        sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, width: 24, height: 24 }}
                    >
                        <Visibility sx={{ fontSize: '0.875rem' }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                    <IconButton
                        size="small"
                        color="success"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(url, title);
                        }}
                        sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' }, width: 24, height: 24 }}
                    >
                        <Download sx={{ fontSize: '0.875rem' }} />
                    </IconButton>
                </Tooltip>
            </MDBox>
        );
    };

    DocumentAction.propTypes = {
        url: PropTypes.string,
        title: PropTypes.string,
        isPdf: PropTypes.bool,
        isWord: PropTypes.bool,
        isImage: PropTypes.bool,
    };

    const columns = React.useMemo(() => {
        const baseColumns = [
            {
                accessorFn: (row) => row.created_at,
                id: "created_at",
                header: "Date",
                size: 200,
                Cell: ({ cell }) => moment(cell.getValue()).format("DD-MM-YYYY"),
                Filter: ({ column }) => (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                format="DD-MM-YYYY"
                                value={column.getFilterValue() ? dayjs(column.getFilterValue()) : null}
                                onChange={(newValue) => {
                                    column.setFilterValue(newValue ? newValue.format("YYYY-MM-DD") : undefined);
                                }}
                                slotProps={{
                                    textField: {
                                        variant: "standard",
                                        fullWidth: true,
                                        placeholder: "DD-MM-YYYY",
                                        InputProps: {
                                            sx: {
                                                fontSize: "12px",
                                                color: "#fff !important",
                                                "&:before": { borderBottomColor: "#fff !important" },
                                                "&:hover:not(.Mui-disabled):before": { borderBottomColor: "#fff !important" },
                                                "&:after": { borderBottomColor: "#fff !important" },
                                                "& input": { fontSize: "5px", height: "5px", padding: "1px 0", color: "#fff !important", WebkitTextFillColor: "#fff !important" },
                                                "& input::placeholder": { color: "#fff !important", opacity: 0.7 },
                                            },
                                        },
                                        sx: { "& .MuiIconButton-root": { color: "#fff" } },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                        {column.getFilterValue() && (
                            <Tooltip title="Clear date filter">
                                <IconButton
                                    size="small"
                                    onClick={() => column.setFilterValue(undefined)}
                                    sx={{ color: "#fff", width: 18, height: 18, p: 0 }}
                                >
                                    <Close sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                ),
                filterFn: (row, id, filterValue) => {
                    if (!filterValue) return true;
                    // Format both to same format for string comparison
                    const rowDate = moment(row.getValue(id)).format("YYYY-MM-DD");
                    return rowDate === filterValue;
                },
            },
            {
                accessorFn: (row) => row.subject,
                id: "subject",
                header: "Subject",
                size: 300,
                filterFn: "contains",
                Cell: ({ cell }) => {
                    const subject = cell.getValue() || "";
                    const limit = 35;
                    const truncated = subject.length > limit ? subject.substring(0, limit) + "..." : subject;
                    return (
                        <Tooltip title={subject} arrow>
                            <MDTypography variant="button" fontWeight="medium" color="dark" sx={{ cursor: 'pointer' }}>
                                {truncated}
                            </MDTypography>
                        </Tooltip>
                    );
                },
            },
            {
                accessorFn: (row) => row.message,
                id: "message",
                header: "Message",
                size: 450,
                filterFn: "contains",
                Cell: ({ cell }) => {
                    const msg = cell.getValue() || "";
                    const tempDiv = document.createElement("div");
                    tempDiv.innerHTML = msg;
                    const text = tempDiv.textContent || tempDiv.innerText || "";
                    const limit = 50;
                    const truncated = text.length > limit ? text.substring(0, limit) + "..." : text;
                    return (
                        <Tooltip title={<Box sx={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: msg }} />} arrow>
                            <MDTypography variant="button" color="text" sx={{ display: 'block', whiteSpace: 'normal', cursor: 'pointer', lineHeight: 1.4 }}>
                                {truncated}
                            </MDTypography>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: "attachments",
                header: "Attachments",
                size: 200,
                enableColumnFilter: false,
                Cell: ({ cell }) => {
                    const attachments = cell.getValue();
                    let files = [];
                    try {
                        files = typeof attachments === 'string' ? JSON.parse(attachments) : attachments;
                    } catch (e) {
                        files = [];
                    }

                    if (!files || files.length === 0)
                        return (
                            <Typography variant="caption" color="text">
                                No media
                            </Typography>
                        );

                    return (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            {files.map((file, idx) => {
                                const lowerUrl = file.url?.toLowerCase() || "";
                                const cleanUrl = lowerUrl.split('?')[0];
                                const isPdf = cleanUrl.endsWith(".pdf");
                                const isWord = cleanUrl.endsWith(".doc") || cleanUrl.endsWith(".docx");
                                const isImage = ["jpg", "jpeg", "png", "gif", "svg", "webp"].some(ext => cleanUrl.endsWith(`.${ext}`));
                                return (
                                    <DocumentAction
                                        key={idx}
                                        url={file.url}
                                        title={file.name || "Attachment"}
                                        isPdf={isPdf}
                                        isWord={isWord}
                                        isImage={isImage}
                                    />
                                );
                            })}
                        </Box>
                    );
                },
            },
        ];


        if (user?.role_id !== 3) {
            baseColumns.push({
                accessorKey: "recipient_names",
                header: "Email Receivers",
                size: 250,
                enableColumnFilter: false,
                enableSorting: false,
                Cell: ({ cell, row }) => {
                    const names = cell.getValue() || [];
                    if (names.length === 0) return "-";

                    if (names.includes("All Users")) {
                        return <Chip label="All Users" size="small" color="info" variant="gradient" />;
                    }

                    if (names.length <= 2) {
                        return (
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                {names.map((name, idx) => (
                                    <Chip key={idx} label={name} size="small" variant="outlined" />
                                ))}
                            </Box>
                        );
                    }

                    return (
                        <Box display="flex" gap={0.5} alignItems="center">
                            <Chip label={names[0]} size="small" variant="outlined" />
                            <Chip
                                label={`+${names.length - 1} more`}
                                size="small"
                                color="info"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setRecipientList(names);
                                    setRecipientDialogOpen(true);
                                }}
                                sx={{ cursor: "pointer" }}
                            />
                        </Box>
                    );
                },
            });
        }

        // Actions column for all users
        baseColumns.push({
            id: "actions",
            header: "Actions",
            size: 150,
            enableColumnFilter: false,
            Cell: ({ row }) => (
                <Box display="flex" gap={1}>
                    <Tooltip title="View Details">
                        <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                                setSelectedBroadcast(row.original);
                                setPreviewOpen(true);
                            }}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {/* Only Admin can resend */}
                    {(user?.role_id === 1 || user?.role_id === 2) && (
                        <Tooltip title="Resend (Click to Edit and Resend)">
                            <IconButton
                                size="small"
                                color="success"
                                onClick={() => onEdit && onEdit(row.original)}
                            >
                                <Replay fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        });

        return baseColumns;
    }, [user, onEdit]);

    return (
        <MDBox p={3}>
            <MaterialReactTable
                columns={columns}
                data={data}
                layoutMode="grid"
                state={{ isLoading: loading }}
                initialState={{
                    density: "compact",
                    pagination: { pageSize: 10 },
                    showColumnFilters: true
                }}
                enableGlobalFilter={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableColumnFilters
                enableSorting
                muiTableHeadProps={{
                    sx: {
                        padding: "0px",
                        backgroundColor: "#011F3A",
                    },
                }}
                muiTableHeadCellProps={{
                    sx: {
                        backgroundColor: "#004030",
                        color: "#fff",
                        fontWeight: "bold",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        "& .MuiTableSortLabel-root": {
                            "& .MuiTableSortLabel-icon": {
                                color: "#fff !important",
                            },
                        },
                        "& .MuiInputBase-input": {
                            color: "#fff !important",
                            fontSize: "12px",
                            "::placeholder": {
                                color: "#e0e0e0",
                                opacity: 1,
                            },
                        },
                        "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                            color: "#ffffff",
                        },
                        "& .MuiIconButton-root": {
                            color: "#fff",
                        },
                        "& .MuiInput-underline:before": {
                            borderBottomColor: "#e0e0e0",
                        },
                        "& .MuiInput-underline:after": {
                            borderBottomColor: "#fff",
                        },
                        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                            borderBottomColor: "#fff",
                        },

                        "& .MuiCheckbox-root": {
                            color: "#fff",
                        },
                    },
                }}
                muiTableBodyCellProps={{
                    sx: {
                        padding: "8px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    },
                }}
                muiTableBodyRowProps={({ row }) => ({
                    sx: {
                        backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0",
                    },
                })}
            />

            {/* Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    },
                }}
            >
                <DialogTitle sx={{ p: 3, pb: 0 }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Icon color="info" fontSize="medium">
                                email
                            </Icon>
                            <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                                Email Preview
                            </MDTypography>
                        </Box>
                        <IconButton
                            onClick={() => setPreviewOpen(false)}
                            aria-label="close preview"
                            sx={{ color: "text.secondary" }}
                        >
                            <Close />
                        </IconButton>
                    </MDBox>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {selectedBroadcast && (
                        <Box display="flex" flexDirection="column" gap={3}>
                            {/* Header Section: Subject & Date */}
                            <Box>
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    color="text"
                                    textTransform="uppercase"
                                    mb={1}
                                    display="block"
                                >
                                    Subject
                                </MDTypography>
                                <Box
                                    sx={{
                                        bgcolor: "grey.50",
                                        p: 2,
                                        borderRadius: "12px",
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                    }}
                                >
                                    <MDTypography variant="h6" fontWeight="bold" color="dark" gutterBottom>
                                        {selectedBroadcast.subject}
                                    </MDTypography>

                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Icon fontSize="small" sx={{ color: "text.secondary", fontSize: 18 }}>
                                            event
                                        </Icon>
                                        <MDTypography variant="button" color="text" fontWeight="regular">
                                            Sent on{" "}
                                            {moment(selectedBroadcast.created_at).format("DD MMMM YYYY [at] hh:mm A")}
                                        </MDTypography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Message Body */}
                            <Box>
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    color="text"
                                    textTransform="uppercase"
                                    mb={1}
                                >
                                    Message Body
                                </MDTypography>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        bgcolor: "#fff",
                                        border: "1px solid",
                                        borderColor: "grey.200",
                                        borderRadius: "12px",
                                        minHeight: "150px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            whiteSpace: "pre-wrap",
                                            fontFamily: "inherit",
                                            lineHeight: 1.6,
                                        }}
                                        dangerouslySetInnerHTML={{ __html: selectedBroadcast.message }}
                                    />
                                </Paper>
                            </Box>

                            {/* Attachments Section */}
                            {(() => {
                                let attachments = [];
                                try {
                                    attachments =
                                        typeof selectedBroadcast.attachments === "string"
                                            ? JSON.parse(selectedBroadcast.attachments)
                                            : selectedBroadcast.attachments;
                                } catch (e) {
                                    attachments = [];
                                }

                                if (attachments && attachments.length > 0) {
                                    return (
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                                                <Icon fontSize="small" sx={{ color: "text.secondary" }}>
                                                    attachment
                                                </Icon>
                                                <MDTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color="text"
                                                    textTransform="uppercase"
                                                >
                                                    Attachments ({attachments.length})
                                                </MDTypography>
                                            </Box>
                                            <Box display="flex" flexWrap="wrap" gap={2}>
                                                {attachments.map((file, index) => {
                                                    const lowerUrl = file.url?.toLowerCase() || "";
                                                    const cleanUrl = lowerUrl.split('?')[0];
                                                    const isPdf = cleanUrl.endsWith(".pdf");
                                                    const isWord = cleanUrl.endsWith(".doc") || cleanUrl.endsWith(".docx");
                                                    const isImage = ["jpg", "jpeg", "png", "gif", "svg", "webp"].some(ext => cleanUrl.endsWith(`.${ext}`));
                                                    return (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                textDecoration: "none",
                                                                cursor: "pointer",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                                bgcolor: "background.paper",
                                                                borderRadius: "12px",
                                                                p: 1.5,
                                                                border: "1px solid",
                                                                borderColor: "grey.200",
                                                                transition: "all 0.2s ease",
                                                                minWidth: "220px",
                                                                "&:hover": {
                                                                    borderColor: "info.main",
                                                                    transform: "translateY(-2px)",
                                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                                                },
                                                            }}
                                                        >
                                                            <DocumentAction
                                                                url={file.url}
                                                                title={file.name || "Attachment"}
                                                                isPdf={isPdf}
                                                                isWord={isWord}
                                                                isImage={isImage}
                                                            />
                                                            <Box overflow="hidden" onClick={() => handlePreview(file.url)}>
                                                                <MDTypography
                                                                    variant="button"
                                                                    fontWeight="medium"
                                                                    color="dark"
                                                                    display="block"
                                                                    sx={{
                                                                        maxWidth: 150,
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {file.name || "Attachment"}
                                                                </MDTypography>
                                                                <MDTypography variant="caption" color="text">
                                                                    View / Download
                                                                </MDTypography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                }
                                return null;
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <MDButton variant="gradient" color="info" onClick={() => setPreviewOpen(false)}>
                        Close Preview
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Document Preview Dialog */}
            <Dialog
                open={Boolean(viewPdfUrl)}
                onClose={() => setViewPdfUrl(null)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Document Preview
                    <IconButton onClick={() => setViewPdfUrl(null)}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
                    {viewPdfUrl && (
                        <iframe
                            src={viewPdfUrl}
                            width="100%"
                            height="100%"
                            title="Document Preview"
                            style={{ border: 'none' }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Recipients List Dialog */}
            <Dialog open={recipientDialogOpen} onClose={() => setRecipientDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        Recipient List
                        <IconButton onClick={() => setRecipientDialogOpen(false)} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap={1}>
                        {recipientList.map((name, idx) => (
                            <Box key={idx} py={1} px={2} bgcolor="grey.100" borderRadius={1} border="1px solid" borderColor="grey.200">
                                <Typography variant="button" fontWeight="medium">
                                    {name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setRecipientDialogOpen(false)} color="info">Close</MDButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(message.status)}
                autoHideDuration={3000}
                onClose={() => setMessage({ status: "", title: "", msg: "" })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setMessage({ status: "", title: "", msg: "" })}
                    severity={message.status}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {message.msg}
                </Alert>
            </Snackbar>
        </MDBox>
    );
};

export default BroadcastHistory;
